import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, TextInput, Platform, ScrollView } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppDispatch, useAppSelector } from '@/store';
import { profileStrings as strings } from '@/constants/residents/strings';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { launchImageLibrary, ImagePickerResponse } from 'react-native-image-picker';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { setUser } from '@/store/slices/authSlice';
import KeyboardSafeView from '@/components/common/KeyboardSafeView';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { showAlert } from '@/utils/common/alert';

export const ProfileBioScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const insets = useSafeAreaInsets();

  const displayName = useMemo(() => {
    if (!user) return '';
    if (user.fullName && user.fullName.trim().length > 0) return user.fullName;
    const composed = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return composed || user.email;
  }, [user]);

  const initialBio = (user as any)?.roommateBio || '';
  const [bio, setBio] = useState<string>(initialBio);
  const [isSaving, setIsSaving] = useState(false);
  const [localPhotoUri, setLocalPhotoUri] = useState<string | null>(null);

  const photoSource = useMemo(() => {
    if (localPhotoUri) return { uri: localPhotoUri } as const;
    if (user?.photoUrl || user?.profilePictureUrl) {
      return { uri: (user?.photoUrl || user?.profilePictureUrl) as string } as const;
    }
    return null;
  }, [localPhotoUri, user?.photoUrl, user?.profilePictureUrl]);

  const onPickPhoto = useCallback(() => {
    const options = {
      mediaType: 'photo' as const,
      quality: 0.9 as const,
      selectionLimit: 1,
      includeBase64: true,
      maxWidth: 150,
      maxHeight: 150,
    };
    launchImageLibrary(options, async (response: ImagePickerResponse) => {
      if (response.didCancel || response.errorCode) {
        return;
      }
      const asset = response.assets && response.assets[0];
      if (!asset) return;
      if (asset.uri) {
        setLocalPhotoUri(asset.uri);
      }
      try {
        if (!user?.id) {
          throw new Error('Missing user id');
        }
        if (asset.base64) {
          const updated = await AuthAPI.updateProfileImage(user.id, asset.base64);
          dispatch(setUser(updated as any));
          showAlert('Success', 'Profile photo updated');
        }
      } catch (e: any) {
        const msg = e?.message || 'Failed to update photo';
        showAlert('Error', msg);
      }
    });
  }, [dispatch, user?.id]);

  const onSave = useCallback(async () => {
    try {
      setIsSaving(true);
      if (!user?.id) {
        throw new Error('Missing user id');
      }
      const updated = await AuthAPI.updateBio(user.id, bio);
      dispatch(setUser(updated as any));
      showAlert('Success', 'Saved');
    } catch (e: any) {
      const msg = e?.message || 'Failed to save';
      showAlert('Error', msg);
    } finally {
      setIsSaving(false);
    }
  }, [bio, dispatch]);

  return (
    <KeyboardSafeView>
      <KeyboardDismissWrapper style={{ flex: 1 }}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={[styles.contentContainer, { paddingBottom: insets.bottom + 24 }]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        >

      <View style={styles.avatarBlock}>
        <View style={styles.avatarContainer}>
          {photoSource ? (
            <Image source={photoSource} style={styles.avatar} />
          ) : (
            <RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER width={56} height={56} />
          )}
          <TouchableOpacity style={styles.editButton} onPress={onPickPhoto} accessibilityLabel="Edit Profile Photo">
            <Icon name="pencil" size={16} color="#000" />
          </TouchableOpacity>
        </View>
        <Text style={styles.name}>{displayName}</Text>
        {!!user?.email && <Text style={styles.sub}>{user.email}</Text>}
      </View>

      <View style={styles.bioBlock}>
        <TextInput
          value={bio}
          onChangeText={setBio}
          placeholder={strings.bio.placeholder}
          placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY}
          multiline
          style={styles.bioInput}
        />
      </View>

      <TouchableOpacity style={styles.saveButton} onPress={onSave} disabled={isSaving} accessibilityRole="button">
        <Text style={styles.saveButtonText}>{isSaving ? 'Saving...' : strings.bio.button.save}</Text>
      </TouchableOpacity>
        </ScrollView>
      </KeyboardDismissWrapper>
    </KeyboardSafeView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  contentContainer: { padding: 16 },
  header: {
    paddingVertical: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
  },
  avatarBlock: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  avatarContainer: {
    height: 100,
    width: 100,
    borderRadius: 50,
    borderWidth: 1,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#D1D1D6',
    position: 'relative',
  },
  avatar: {
    height: 98,
    width: 98,
    borderRadius: 49,
  },
  name: {
    fontSize: 16,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginTop: 12,
    textAlign: 'center',
  },
  sub: {
    fontSize: 13,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.8,
    marginTop: 4,
    textAlign: 'center',
  },
  editButton: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    height: 36,
    width: 36,
    borderRadius: 18,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
    borderWidth: 1,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bioBlock: {
    marginTop: 16,
  },
  bioInput: {
    minHeight: 200,
    borderWidth: 1,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    borderRadius: 8,
    padding: 12,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
    textAlignVertical: 'top',
    fontSize: 15,
  },
  saveButton: {
    marginTop: 20,
    alignItems: 'center',
    paddingVertical: 12,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    borderRadius: 8,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
});

export default ProfileBioScreen;

